import { useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import type { Point } from '../../doc/geometry';
import type { SheetItem } from '../../doc/schema';
import { useBoard, usePane, usePaneStore, useReadOnly } from '../context';
import { worldToItemLocal } from '../cutout';
import { flash } from '../flash';
import { floodFill, pickColor, StrokeSession, type BrushParams } from '../paint/brushes';
import { rasterStore, type LiveLayer } from '../paint/rasterStore';
import { enterPaint, isSheet } from '../paint/session';
import { setToolSettings, toolSettings } from '../toolSettings';
import { screenToWorld } from './camera';
import { isRejectedTouch } from './DrawOverlay';

/**
 * Screen-space canvas for painting on sketch sheets (the sheet tool in paint mode). A press on
 * the sheet being painted starts a stroke on its active layer; a press on another sheet makes
 * that one the target; a press off any sheet falls through to the viewport and pans, like the
 * draw overlay. The brush outline follows the pointer.
 */
export function PaintOverlay() {
  const active = usePane((s) => s.tool === 'sheet' && s.paintSheetId !== null);
  const readOnly = useReadOnly();
  if (!active || readOnly) return null;
  return <PaintCanvas />;
}

interface Stroke {
  pointerId: number;
  live: LiveLayer;
  session: StrokeSession;
  /** The sheet's geometry at the press (the stroke keeps mapping to it). */
  sheet: SheetItem;
}

function PaintCanvas() {
  const board = useBoard();
  const pane = usePaneStore();
  const size = usePane((s) => s.size);
  const store = rasterStore(board);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stroke = useRef<Stroke | null>(null);
  const liveTouch = useRef(false);
  const hover = useRef<Point | null>(null);
  const dpr = window.devicePixelRatio || 1;

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.width = Math.round(size.w * dpr);
    c.height = Math.round(size.h * dpr);
    render();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size, dpr]);

  // Safari runs its touch defaults for pointers we ignore; cancel them at the touch level.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const swallow = (e: TouchEvent) => {
      if (e.cancelable) e.preventDefault();
    };
    const opts: AddEventListenerOptions = { passive: false };
    c.addEventListener('touchstart', swallow, opts);
    c.addEventListener('touchmove', swallow, opts);
    c.addEventListener('touchend', swallow, opts);
    return () => {
      c.removeEventListener('touchstart', swallow);
      c.removeEventListener('touchmove', swallow);
      c.removeEventListener('touchend', swallow);
    };
  }, []);

  // the brush outline is in screen space: redraw when the camera or the brush changes
  useEffect(() => {
    const a = pane.subscribe((s, prev) => {
      if (s.camera !== prev.camera) render();
    });
    const b = toolSettings.subscribe(() => render());
    return () => {
      a();
      b();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pane]);

  const toLocal = (e: { clientX: number; clientY: number }): Point => {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  /** The sheet under a world point: the one being painted first, else the topmost visible one. */
  const sheetAt = (world: Point): SheetItem | null => {
    const s = pane.getState();
    const current = s.paintSheetId ? board.index.get(s.paintSheetId) : undefined;
    if (isSheet(current) && contains(current, world)) return current;
    const hits = board.index.query({ x: world.x - 0.5, y: world.y - 0.5, w: 1, h: 1 });
    for (let i = hits.length - 1; i >= 0; i--) {
      const it = hits[i]!;
      if (isSheet(it) && !board.isHidden(it) && !board.isLocked(it) && contains(it, world)) return it;
    }
    return null;
  };

  const render = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, c.width / dpr, c.height / dpr);
    const h = hover.current;
    if (!h) return;
    const s = pane.getState();
    const sheet = s.paintSheetId ? board.index.get(s.paintSheetId) : undefined;
    if (!isSheet(sheet)) return;
    const ts = toolSettings.getState();
    const accent = getComputedStyle(c).getPropertyValue('--accent').trim() || '#2b5bd7';
    if (ts.brush === 'fill' || ts.brush === 'picker') {
      ctx.strokeStyle = accent;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(h.x - 8, h.y);
      ctx.lineTo(h.x + 8, h.y);
      ctx.moveTo(h.x, h.y - 8);
      ctx.lineTo(h.x, h.y + 8);
      ctx.stroke();
      return;
    }
    const b = ts.brushes[ts.brush];
    const r = ((b.size / 2) * sheet.w * s.camera.zoom) / Math.max(1, sheet.props.pxW);
    ctx.beginPath();
    ctx.arc(h.x, h.y, Math.max(1.5, r), 0, Math.PI * 2);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2.5;
    ctx.globalAlpha = 0.6;
    ctx.stroke();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = '#111111';
    ctx.lineWidth = 1;
    ctx.stroke();
  };

  /** World point to a layer pixel of the sheet. */
  const toPixel = (sheet: SheetItem, world: Point): Point => {
    const l = worldToItemLocal(sheet, world);
    return { x: (l.x * sheet.props.pxW) / Math.max(1, sheet.w), y: (l.y * sheet.props.pxH) / Math.max(1, sheet.h) };
  };

  const pressureOf = (e: { pointerType: string; pressure: number }): number => (e.pointerType === 'pen' ? Math.max(0.05, e.pressure) : 1);

  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const st = stroke.current;
    if (st) {
      // a second finger beside a painting finger is a gesture (pinch, two-finger tap), not paint
      if (e.pointerType === 'touch' && liveTouch.current) {
        stroke.current = null;
        if (e.currentTarget.hasPointerCapture(st.pointerId)) e.currentTarget.releasePointerCapture(st.pointerId);
        st.session.cancel();
        store.cancelStroke(st.live);
        if (st.live.scratch) st.live.scratch.style.opacity = '';
      }
      return;
    }
    const s = pane.getState();
    if (e.button !== 0 && e.pointerType !== 'touch') return; // middle/right: fall through (pan)
    if (s.spaceHeld) return;
    if (isRejectedTouch(e.pointerType)) return;
    const local = toLocal(e);
    const world = screenToWorld(s.camera, local);
    const sheet = sheetAt(world);
    if (!sheet) return; // off every sheet: the viewport pans
    e.stopPropagation();
    const el = e.currentTarget;
    if (sheet.id !== s.paintSheetId) {
      // switch to this sheet; its layer loads and the next press paints
      enterPaint({ board, pane }, sheet.id);
      flash(el, `Painting on this sheet`);
      return;
    }
    const layerId = s.paintLayerId;
    const def = layerId ? sheet.props.layers[layerId] : undefined;
    const live = store.active(sheet.id);
    if (!layerId || !def || !live || live.layerId !== layerId) {
      flash(el, 'Loading the layer…');
      return;
    }
    if (live.unavailable) return flash(el, 'This layer is not available offline yet');
    if (!live.loaded) return flash(el, 'Loading the layer…');
    if (def.locked) return flash(el, 'The layer is locked');
    if (!def.visible) return flash(el, 'The layer is hidden');
    const ts = toolSettings.getState();
    const kind = e.altKey ? 'picker' : ts.brush;
    const px = toPixel(sheet, world);
    if (kind === 'picker') {
      const color = pickColor(live.canvas, px.x, px.y) ?? sheet.props.paper;
      if (color) {
        setToolSettings({ paintColor: color });
        flash(el, `Colour ${color}`);
      } else flash(el, 'Nothing to pick here');
      return;
    }
    if (kind === 'fill') {
      const backup = store.beginStroke(live);
      const rect = floodFill(live.canvas, px.x, px.y, ts.paintColor, ts.brushes.fill.opacity, ts.fillTolerance, backup);
      store.endStroke(live, rect);
      return;
    }
    if (!live.scratch) return;
    el.setPointerCapture(e.pointerId);
    liveTouch.current = e.pointerType === 'touch';
    const params: BrushParams = { ...ts.brushes[kind], kind, color: ts.paintColor };
    const session = new StrokeSession(live.canvas, live.scratch, params, store.beginStroke(live));
    live.scratch.style.opacity = String(params.opacity);
    session.begin({ x: px.x, y: px.y, pressure: pressureOf(e) });
    stroke.current = { pointerId: e.pointerId, live, session, sheet };
    hover.current = local;
    render();
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const st = stroke.current;
    const s = pane.getState();
    if (!st) {
      if (e.pointerType === 'touch') return;
      hover.current = toLocal(e);
      render();
      return;
    }
    if (st.pointerId !== e.pointerId) return;
    const native = e.nativeEvent;
    const events = typeof native.getCoalescedEvents === 'function' ? native.getCoalescedEvents() : [];
    const list = events.length ? events : [native];
    for (const ev of list) {
      const world = screenToWorld(s.camera, toLocal(ev));
      const px = toPixel(st.sheet, world);
      st.session.extend({ x: px.x, y: px.y, pressure: pressureOf(ev) });
    }
    hover.current = toLocal(e);
    render();
  };

  const finish = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const st = stroke.current;
    if (!st || st.pointerId !== e.pointerId) return;
    stroke.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    const rect = st.session.end();
    if (st.live.scratch) st.live.scratch.style.opacity = '';
    store.endStroke(st.live, rect);
    if (e.pointerType === 'touch') hover.current = null;
    render();
  };

  return (
    <canvas
      ref={canvasRef}
      className="draw-overlay paint-overlay"
      style={{ width: size.w, height: size.h }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finish}
      onPointerCancel={finish}
      onPointerLeave={() => {
        if (!stroke.current) {
          hover.current = null;
          render();
        }
      }}
    />
  );
}

function contains(sheet: SheetItem, world: Point): boolean {
  const l = worldToItemLocal(sheet, world);
  return l.x >= 0 && l.y >= 0 && l.x <= sheet.w && l.y <= sheet.h;
}
